import { useEffect } from 'react';
import type { StepProps } from '../engine/context.ts';
import { TitleBlock } from '../ui/TitleBlock.tsx';
import { StatTile, VideoFrame } from '../ui/bits.tsx';
import { Icon } from '../ui/Icon.tsx';
import { asset } from '../engine/assets.ts';
import { CREAM, GREEN_BG, GREEN_FG, MUTED, PAPER, SHADOW_CARD, YELLOW, GRAD } from '../ui/theme.ts';

export function Hook({ step, content, ctx }: StepProps) {
  const hook = ctx.shared.hook!;

  useEffect(() => {
    ctx.setCta({
      label: content.cta ?? 'Start my plan',
      caption: content.ctaCaption,
      onPress: () => {
        ctx.event('funnel_start');
        ctx.next();
      },
    });
  }, [step.id, content.cta, content.ctaCaption]);

  return (
    <>
      <TitleBlock title={content.title} highlight={content.highlight} subtitle={content.subtitle} top={20} />

      <VideoFrame
        slot={hook.asset}
        maxHeight={176}
        muteLabel={ctx.shared.ui?.mutedLoop ?? 'muted · loop'}
        overlay={
          <>
            <span style={{ position: 'absolute', left: 10, top: 10, padding: '4px 9px', borderRadius: 999, background: 'rgba(255,255,255,.92)', fontSize: 11, fontWeight: 800 }}>
              {ctx.shared.ui?.before ?? 'Before'}
            </span>
            <span style={{ position: 'absolute', left: 'calc(50% + 10px)', top: 10, padding: '4px 9px', borderRadius: 999, background: GRAD, color: '#fff', fontSize: 11, fontWeight: 800 }}>
              {ctx.shared.ui?.after ?? 'After'}
            </span>
          </>
        }
      />

      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', paddingLeft: 10 }}>
          {hook.avatars.map((slot) => (
            <img
              key={slot}
              src={asset(slot)}
              alt=""
              width={34}
              height={34}
              style={{ width: 'clamp(28px, 8vw, 34px)', height: 'clamp(28px, 8vw, 34px)', borderRadius: '50%', border: '3px solid #fff', marginLeft: -10, objectFit: 'cover' }}
            />
          ))}
        </span>
        {hook.rating.value ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: 'clamp(5px, 1.6vw, 8px) clamp(10px, 3vw, 14px)', borderRadius: 999, background: CREAM }}>
            <span style={{ display: 'inline-flex', gap: 1, color: YELLOW }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Icon key={i} name="star" size={14} />
              ))}
            </span>
            <span style={{ fontWeight: 800 }}>{hook.rating.value}</span>
            {hook.rating.caption ? <span style={{ fontSize: 14, color: MUTED, fontWeight: 600 }}>{hook.rating.caption}</span> : null}
          </span>
        ) : null}
      </div>

      {hook.stats.length ? (
        <div style={{ display: 'grid', gridTemplateColumns: hook.stats.length > 1 ? '1fr 1fr' : '1fr', gap: 10 }}>
          {hook.stats.map((s, i) => (
            <StatTile key={i} value={s.value} caption={s.caption} highlight={i === 1} />
          ))}
        </div>
      ) : null}

      {hook.testimonial.quote ? (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: 'var(--fk-card-pad)', borderRadius: 20, background: PAPER, boxShadow: SHADOW_CARD }}>
          <img src={asset(hook.testimonial.avatar)} alt="" width={40} height={40} style={{ width: 'clamp(32px, 9vw, 40px)', height: 'clamp(32px, 9vw, 40px)', borderRadius: '50%', objectFit: 'cover' }} />
          <div style={{ fontSize: 15, fontWeight: 600, flex: 1, lineHeight: 1.35 }}>
            “{hook.testimonial.quote}”
            {hook.testimonial.name ? (
              <div style={{ color: MUTED, fontWeight: 700, fontSize: 14, marginTop: 2 }}>
                {[hook.testimonial.name, hook.testimonial.niche].filter(Boolean).join(' · ')}
              </div>
            ) : null}
          </div>
          {hook.testimonial.result ? (
            <span style={{ padding: '4px 9px', borderRadius: 999, background: GREEN_BG, color: GREEN_FG, fontSize: 11, fontWeight: 800, flex: 'none' }}>
              {hook.testimonial.result}
            </span>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
